/**
 * Partner Beta Banner
 *
 * Thin band rendered above the header when the host is a partner
 * subdomain on the beta environment (e.g. homedepotcan.beta.estimator
 * .trusscore.com). Visible enough that a partner stakeholder can't
 * mistake the preview environment for their live portal, dismissable
 * for the session if they're actively reviewing.
 *
 * No-op on the apex beta (beta.estimator.trusscore.com) — the existing
 * Trusscore site already signals environment elsewhere; this banner
 * is specifically about preventing partner-portal confusion.
 */

import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';

const DISMISS_KEY = 'trusscore_partner_beta_banner_dismissed';

export const PartnerBetaBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const { partner, isBeta } = usePartnerBranding();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (!isBeta || !partner || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div
      role="status"
      style={{
        background: '#7c2d12',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'center',
        padding: '6px 36px 6px 12px',
        position: 'relative',
        letterSpacing: 0.3,
      }}
    >
      <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
      BETA PREVIEW — this is a staging environment for testing. Not the live{' '}
      {partner.name} estimator.
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('betaBanner.dismiss')}
        style={{
          position: 'absolute',
          top: '50%',
          right: 8,
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: '#fff',
          padding: 4,
          cursor: 'pointer',
          opacity: 0.85,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
};
